import React from 'react'
import { t } from 'i18next'
import SquareIcon from './ResizeModel/icons/SquareIcon'
import PortraitIcon from './ResizeModel/icons/PortraitIcon'
import LandscapeIcon from './ResizeModel/icons/LandscapeIcon'
import Portrait34Icon from './ResizeModel/icons/Portrait34Icon'
import Portrait45Icon from './ResizeModel/icons/Portrait45Icon'
import Portrait916Icon from './ResizeModel/icons/Portrait916Icon'
import AddMenuResizeIcon from '@/scenes/Editor/components/Icons/AddMenuResizeIcon'
import type { ButtonListObject } from './ButtonsListPopover'

// ─── Ratio config ─────────────────────────────────────────────────────────────

export const ASPECT_RATIOS = {
  Square: 1,
  '4:5':  4 / 5,
  '3:4':  3 / 4,
  '2:3':  2 / 3,
  '9:16': 9 / 16,
} as const

export type AspectRatioKey = keyof typeof ASPECT_RATIOS

export const DEFAULT_ASPECT_RATIO: AspectRatioKey = '3:4'

/** Maps ratio key to the AI fashion session aspectRatioOption string. */
export const ASPECT_RATIO_MAP: Record<AspectRatioKey, 'square' | 'portrait' | 'portrait_4_5' | 'portrait_2_3' | 'portrait_9_16'> = {
  Square:  'square',
  '4:5':   'portrait_4_5',
  '3:4':   'portrait',
  '2:3':   'portrait_2_3',
  '9:16':  'portrait_9_16',
}

/** Numeric aspect ratio indexed by session option string. Derived from ASPECT_RATIO_MAP and ASPECT_RATIOS. */
export const ASPECT_RATIO_VALUES: Record<string, number> = Object.fromEntries(
  (Object.keys(ASPECT_RATIOS) as AspectRatioKey[]).map(
    uiKey => [ASPECT_RATIO_MAP[uiKey], ASPECT_RATIOS[uiKey]]
  )
)

export const RATIO_ICON_MAP: Record<string, React.ReactElement> = {
  Square:    React.createElement(SquareIcon),
  Portrait:  React.createElement(PortraitIcon),
  Landscape: React.createElement(LandscapeIcon),
  '4:5':     React.createElement(Portrait45Icon),
  '3:4':     React.createElement(Portrait34Icon),
  '2:3':     React.createElement(PortraitIcon),
  '9:16':    React.createElement(Portrait916Icon),
}

/** Human-readable subtitle for each ratio key, shown as a badge in the size popover. */
export const RATIO_SUBTITLE_MAP: Record<string, string> = {
  Square: 'Marketplaces',
  '4:5':  'Instagram',
  '3:4':  'Catalog',
  '2:3':  'Portrait photo',
  '9:16': 'Stories & TikTok',
}

/**
 * Canonical static ratio items (no onClick). Both the AI fashion size popover
 * and the photoshoot size popover are built from this single source.
 */
export function getRatioBaseItems(): ButtonListObject[] {
  return [
    { icon: RATIO_ICON_MAP['Square'], txt: t('Square'), badge: t(RATIO_SUBTITLE_MAP['Square']), value: 'Square', closePopoverOnClick: true },
    { icon: RATIO_ICON_MAP['4:5'],    txt: '4:5',       badge: t(RATIO_SUBTITLE_MAP['4:5']),    value: '4:5',    closePopoverOnClick: true },
    { icon: RATIO_ICON_MAP['3:4'],    txt: '3:4',       badge: t(RATIO_SUBTITLE_MAP['3:4']),    value: '3:4',    closePopoverOnClick: true },
    { icon: RATIO_ICON_MAP['2:3'],    txt: '2:3',       badge: t(RATIO_SUBTITLE_MAP['2:3']),    value: '2:3',    closePopoverOnClick: true },
    { icon: RATIO_ICON_MAP['9:16'],   txt: '9:16',      badge: t(RATIO_SUBTITLE_MAP['9:16']),   value: '9:16',   closePopoverOnClick: true },
  ]
}

/**
 * Sentinel for "keep the source image's own ratio". Not a member of
 * `ASPECT_RATIOS` on purpose — it maps to a null session aspect ratio.
 */
export const ORIGINAL_RATIO_VALUE = 'original'

/**
 * Ratio items prefixed with an "Original size" entry, for surfaces where not
 * resizing is a real (and default) choice — the Batch AI tools panels.
 */
export function getRatioBaseItemsWithOriginal(): ButtonListObject[] {
  return [
    {
      icon: React.createElement(AddMenuResizeIcon),
      txt: t('Original size'),
      value: ORIGINAL_RATIO_VALUE,
      closePopoverOnClick: true,
    },
    ...getRatioBaseItems(),
  ]
}

/** Returns the translated display label for a ratio value, including "Original size". */
export function getRatioLabelWithOriginal(value: string): string {
  return getRatioBaseItemsWithOriginal().find(i => i.value === value)?.txt ?? value
}

/** Returns the icon for a ratio value, including the original-size sentinel. */
export function getRatioIconWithOriginal(value: string): React.ReactElement {
  return getRatioBaseItemsWithOriginal().find(i => i.value === value)?.icon
    ?? React.createElement(AddMenuResizeIcon)
}

/** Returns ratio items with onClick wired to the provided callback. */
export function getRatioItems(onSelect: (value: string) => void): ButtonListObject[] {
  return getRatioBaseItems().map(item => ({
    ...item,
    onClick: () => onSelect(item.value),
  }))
}

/** Returns the translated display label for the given ratio value. */
export function getRatioLabel(value: string): string {
  return getRatioBaseItems().find(i => i.value === value)?.txt ?? value
}
